import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getThemeAssets } from '../utils/theme';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const leftSideRef = useRef<HTMLDivElement>(null);

  // Get theme assets from environment
  const themeAssets = getThemeAssets();

  // Step 1 form data (User Credentials)
  const [step1Data, setStep1Data] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    login_type: '1', // 1=email, 2=facebook, 3=google
    oauth_token: '', // OAuth token for types 2 & 3
  });

  // Step 2 form data (Organization Info)
  const [step2Data, setStep2Data] = useState({
    org_name: '',
    website_url: '',
    industry: '',
    address: '',
    org_type: '',
  });

  const industries = [
    'Aerospace',
    'Advertising Agency',
    'Ecommerce',
    'Education',
    'Entertainment',
    'Fashion',
    'Finance',
    'FMCG',
    'Healthcare',
    'Hospitality',
    'Information Technology',
    'Manufacturing',
    'Oil & Gas',
    'Real Estate/Construction',
    'Retail',
    'Telecom',
    'Other',
  ];

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (leftSideRef.current) {
        const rect = leftSideRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    const leftSide = leftSideRef.current;
    if (leftSide) {
      leftSide.addEventListener('mousemove', handleMouseMove);
      return () => leftSide.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  // Check for OAuth signup data on component mount
  useEffect(() => {
    const oauthDataStr = localStorage.getItem('oauth_signup_data');
    if (oauthDataStr) {
      try {
        const oauthData = JSON.parse(oauthDataStr);
        console.log('[Register] Detected OAuth signup data:', oauthData);

        // Pre-fill Step 1 data with OAuth info
        setStep1Data({
          name: oauthData.name || '',
          email: oauthData.email || '',
          username: oauthData.username || oauthData.email || '',
          password: '', // No password for OAuth
          login_type: String(oauthData.login_type || '1'),
          oauth_token: oauthData.oauth_token || '',
        });

        // Automatically advance to Step 2 (organization details)
        setStep(2);
        setError(null);

        // Clear the OAuth data from localStorage
        localStorage.removeItem('oauth_signup_data');
      } catch (e) {
        console.error('[Register] Failed to parse OAuth data:', e);
        localStorage.removeItem('oauth_signup_data');
      }
    }
  }, []);

  const validateStep1 = (): string | null => {
    if (!step1Data.name.trim()) return 'Name is required';
    if (!step1Data.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(step1Data.email)) return 'Invalid email format';
    if (!step1Data.username.trim()) return 'Username is required';

    // Only validate password for email signups (type 1)
    if (step1Data.login_type === '1') {
      if (!step1Data.password) return 'Password is required';
      if (!/^(?=.*[a-zA-Z])(?=.*\d).{8,}$/.test(step1Data.password)) {
        return 'Password must be at least 8 characters with letters and numbers';
      }
    }

    return null;
  };

  const validateStep2 = (): string | null => {
    if (!step2Data.org_name.trim()) return 'Organization name is required';
    if (!step2Data.industry) return 'Industry is required';
    return null;
  };

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateStep2();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

      // Determine signup type from step1Data
      const signupType = step1Data.login_type || '1';

      // Build request payload
      const payload: any = {
        ...step1Data,
        ...step2Data,
      };

      // For OAuth signups (types 2 & 3), include the OAuth token and remove password
      if (signupType === '2' || signupType === '3') {
        payload.token = step1Data.oauth_token;
        delete payload.password; // OAuth users don't have passwords
      }

      console.log('[Register] Submitting signup:', { type: signupType, email: step1Data.email });

      const response = await fetch(`${apiUrl}/auth/signup?type=${signupType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Signup failed');
      }

      // For email signup (type 1), redirect to OTP verification
      // For OAuth signups (types 2 & 3), redirect to dashboard
      if (signupType === '1') {
        // Redirect to OTP verification page with email and name
        const email = data.data?.email || step1Data.email;
        const name = data.data?.name || step1Data.name;
        navigate(`/verify-otp?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}`);
      } else {
        // OAuth signups are auto-authenticated
        if (data.data?.token) {
          // Store token and user data
          localStorage.setItem('crm_auth_token', data.data.token);
          localStorage.setItem('crm_user', JSON.stringify(data.data.user));
        }
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookSignup = () => {
    const clientId = import.meta.env.VITE_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
    const redirectUri = encodeURIComponent(import.meta.env.VITE_FACEBOOK_CALLBACK_URL || 'http://localhost:8000/api/auth/facebook/callback');
    const state = JSON.stringify({ signup: true });

    sessionStorage.setItem('facebook_oauth_state', state);

    const facebookAuthUrl = `https://www.facebook.com/v23.0/dialog/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&state=${encodeURIComponent(state)}&scope=email`;

    window.location.href = facebookAuthUrl;
  };

  const handleGoogleSignup = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '648675563452-ffo4t8hkfovc1kq0qfmls26hvcn76u5o.apps.googleusercontent.com';
    const redirectUri = encodeURIComponent(import.meta.env.VITE_GOOGLE_CALLBACK_URL || 'http://localhost:8000/api/auth/google/callback');
    const state = JSON.stringify({ signup: true });

    sessionStorage.setItem('google_oauth_state', state);

    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=email%20profile&state=${encodeURIComponent(state)}`;

    window.location.href = googleAuthUrl;
  };

  return (
    <div className="h-screen w-screen flex m-0 p-0">
      {/* Left Side - Glass Morphism Cards with Background */}
      <div ref={leftSideRef} className="w-1/2 h-full relative overflow-hidden">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-login-bgLeft"></div>

        {/* Animated Decoration Images */}
        <div
          className="absolute w-24 h-24 transition-transform duration-1000 ease-out opacity-80 hover:opacity-100"
          style={{
            transform: `translate(${mousePosition.x * 60}px, ${mousePosition.y * 40}px) rotate(${mousePosition.x * 10}deg)`,
            top: '10%',
            left: '10%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-16 h-16 transition-transform duration-800 ease-out opacity-60 hover:opacity-90"
          style={{
            transform: `translate(${mousePosition.x * -40}px, ${mousePosition.y * 60}px) rotate(${mousePosition.y * 15}deg)`,
            top: '65%',
            right: '15%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-14 h-14 transition-transform duration-1200 ease-out opacity-50 hover:opacity-80"
          style={{
            transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * -35}px) rotate(${mousePosition.x * -8}deg)`,
            top: '40%',
            left: '75%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-18 h-18 transition-transform duration-900 ease-out opacity-40 hover:opacity-70"
          style={{
            transform: `translate(${mousePosition.x * -25}px, ${mousePosition.y * 20}px) rotate(${mousePosition.y * -12}deg)`,
            top: '80%',
            left: '30%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-12 py-16">
          {/* Main Heading */}
          <div className="text-left mb-12 w-full max-w-lg">
            <h1 className="text-5xl font-bold mb-4 text-white leading-tight">
              Join {themeAssets.brandName}
            </h1>
            <p className="text-xl text-blue-100/90 font-light mb-2">
              Start your journey today
            </p>
            <p className="text-lg text-blue-200/80 font-medium">
              Get started in minutes with our powerful CRM platform.
            </p>
          </div>

          {/* Product Cards */}
          <div className="space-y-6 w-full max-w-lg">
            {/* Lead Management Card */}
            <div
              className="group relative p-6 rounded-2xl backdrop-blur-lg bg-white/10 border border-white/20 shadow-2xl transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:scale-105 cursor-pointer"
              style={{
                transform: `perspective(1000px) rotateX(${mousePosition.y * -3}deg) rotateY(${mousePosition.x * -3}deg)`,
                transitionDelay: '50ms',
              }}
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-green-100 transition-colors text-left">
                    Lead Management
                  </h3>
                  <p className="text-sm text-green-100/80 leading-relaxed text-left">
                    Transform prospects into customers with intelligent tracking and automated pipelines.
                  </p>
                </div>
              </div>
            </div>

            {/* CRM Analytics Card */}
            <div
              className="group relative p-6 rounded-2xl backdrop-blur-lg bg-white/10 border border-white/20 shadow-2xl transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:scale-105 cursor-pointer"
              style={{
                transform: `perspective(1000px) rotateX(${mousePosition.y * 4}deg) rotateY(${mousePosition.x * 4}deg)`,
                transitionDelay: '100ms',
              }}
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-6 h-6 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-100 transition-colors text-left">
                    CRM Analytics
                  </h3>
                  <p className="text-sm text-purple-100/80 leading-relaxed text-left">
                    Data-driven insights to optimize your sales process and maximize revenue growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-1/2 h-full bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center overflow-y-auto">
        <div className="w-full max-w-md mx-8 my-4">
          <div className="">
            <div className="text-center mb-4">
              <img
                src={themeAssets.loginLogo}
                alt={themeAssets.brandName}
                className="h-10 mx-auto mb-3"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-1">
                Create Your Account
              </h1>
              <p className="text-sm text-gray-600">
                {step === 1 ? 'Step 1 of 2: Your Information' : 'Step 2 of 2: Organization Details'}
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Step {step} of 2</span>
                <span className="text-sm text-gray-500">{step === 1 ? 'Personal Info' : 'Organization'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-600 to-blue-700 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(step / 2) * 100}%` }}
                ></div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {step === 1 ? (
              // STEP 1: User Credentials
              <form onSubmit={handleStep1Submit} className="space-y-3">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={step1Data.name}
                    onChange={(e) => setStep1Data({ ...step1Data, name: e.target.value })}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={step1Data.email}
                    onChange={(e) =>
                      setStep1Data({
                        ...step1Data,
                        email: e.target.value,
                        username: e.target.value, // Auto-fill username with email
                      })
                    }
                    placeholder="john@company.com"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={step1Data.password}
                      onChange={(e) => setStep1Data({ ...step1Data, password: e.target.value })}
                      placeholder="Min. 8 characters with letters & numbers"
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                    >
                      {showPassword ? '👁️' : '🔒'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Min. 8 characters with letters and numbers</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Continue to Organization Details
                </button>
              </form>
            ) : (
              // STEP 2: Organization Info
              <form onSubmit={handleStep2Submit} className="space-y-3">
                <div>
                  <label htmlFor="org_name" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    id="org_name"
                    value={step2Data.org_name}
                    onChange={(e) => setStep2Data({ ...step2Data, org_name: e.target.value })}
                    placeholder="Acme Corporation"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Industry
                  </label>
                  <select
                    id="industry"
                    value={step2Data.industry}
                    onChange={(e) => setStep2Data({ ...step2Data, industry: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    <option value="">Select your industry</option>
                    {industries.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Website URL (Optional)
                  </label>
                  <input
                    type="url"
                    id="website_url"
                    value={step2Data.website_url}
                    onChange={(e) => setStep2Data({ ...step2Data, website_url: e.target.value })}
                    placeholder="https://www.yourcompany.com"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Address (Optional)
                  </label>
                  <textarea
                    id="address"
                    value={step2Data.address}
                    onChange={(e) => setStep2Data({ ...step2Data, address: e.target.value })}
                    placeholder="Company address"
                    rows={2}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2.5 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transform hover:scale-105 transition-all duration-200"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-[2] bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </div>
              </form>
            )}

            {step === 1 && (
              <>
                {/* Social Signup Divider */}
                <div className="my-4 flex items-center">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-4 text-sm text-gray-500">or sign up with</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Social Signup Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Facebook Signup */}
                  <button
                    onClick={handleFacebookSignup}
                    disabled={isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span className="text-sm">Facebook</span>
                  </button>

                  {/* Google Signup */}
                  <button
                    onClick={handleGoogleSignup}
                    disabled={isLoading}
                    className="bg-white hover:bg-gray-50 text-gray-900 py-2.5 px-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center space-x-2 border border-gray-300"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span className="text-sm">Google</span>
                  </button>
                </div>
              </>
            )}

            {/* Terms and Privacy */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                By continuing, you agree to our{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700 transition-colors">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700 transition-colors">
                  Privacy Policy
                </a>
              </p>
            </div>

            {/* Footer Links */}
            <div className="mt-3 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:text-blue-700 transition-colors font-medium"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
